// client/src/components/company/RequestDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatInterestRate, formatCurrencyInput, parseCurrencyInput, formatDuration, formatCurrencyWithSymbol, formatSubmittedAt } from '../utils/formatUtils';
import { Info } from 'lucide-react';
import { formatCostAmount, formatDayCountBasis, countDays, DAY_COUNT_DENOMINATORS } from '../vatCredit/vatCreditUtils';
import '../../styles/RequestDetail.css';

// Periodo del deposito: overnight 1 giorno, "Nd" N giorni, custom tra le due date,
// mesi dalla data di allocazione (o da oggi finché la richiesta non è allocata)
const getDepositPeriod = (request) => {
  const { duration } = request;
  if (duration === 'custom') {
    if (!request.customStartDate || !request.customEndDate) return null;
    return { start: new Date(request.customStartDate), end: new Date(request.customEndDate) };
  }

  const start = request.allocationDate ? new Date(request.allocationDate) : new Date();
  const end = new Date(start);
  if (duration === 'overnight') {
    end.setDate(end.getDate() + 1);
  } else if (typeof duration === 'string' && duration.endsWith('d')) {
    end.setDate(end.getDate() + parseInt(duration, 10));
  } else {
    const months = parseInt(duration, 10);
    if (isNaN(months)) return null;
    end.setMonth(end.getMonth() + months);
  }
  return { start, end };
};

// Le offerte legacy non hanno la base di calcolo: si assume ACT/360
const withDayCountBasis = (bid) => ({ ...bid, dayCountBasis: bid.dayCountBasis || 'act_360' });

// Giorni e divisore secondo la base di calcolo dell'offerta
const getBidDayCount = (request, bid) => {
  const period = getDepositPeriod(request);
  if (!period) return null;
  const { dayCountBasis } = withDayCountBasis(bid);
  return { days: countDays(period.start, period.end, dayCountBasis), denominator: DAY_COUNT_DENOMINATORS[dayCountBasis] };
};

// Interessi (attivi per i depositi, passivi per il denaro caldo):
//   importo × tasso effettivo × giorni / divisore della base di calcolo, arrotondato al centesimo
const computeDepositInterest = (amount, effectiveRate, dayCount) => {
  if (!dayCount || effectiveRate === null || effectiveRate === undefined) return null;
  return Math.round(amount * (effectiveRate / 100) * (dayCount.days / dayCount.denominator) * 100) / 100;
};

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Dashboard the user came from (set by the dashboard links), otherwise back to the requests list
  const backToDashboard = useLocation().state?.from;
  const { t, i18n } = useTranslation();
  
  const [request, setRequest] = useState(null);
  const [bids, setBids] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [allocationError, setAllocationError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingAllocation, setProcessingAllocation] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [allocationCompleted, setAllocationCompleted] = useState(false);
  
  // Track if we're currently editing an allocation input
  const [editingInputId, setEditingInputId] = useState(null);
  const [editingInputValue, setEditingInputValue] = useState('');

  // State for affiliated banks
  const [affiliatedBanks, setAffiliatedBanks] = useState([]);
  const [affiliatedBanksLoaded, setAffiliatedBanksLoaded] = useState(false);
  const [thirdPartyBanks, setThirdPartyBanks] = useState([]);

  // Benchmark rate for EUR requests
  const [benchmarkRate, setBenchmarkRate] = useState(null);
  const [benchmarkDate, setBenchmarkDate] = useState(null);

  /* TODO: Commissioni - da abilitare in futuro
  // State for commission info (for non-paying companies)
  const [commissionInfo, setCommissionInfo] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [commissionPreviewData, setCommissionPreviewData] = useState(null);
  */
  
  // Helper function to get the correct bid ID
  const getBidId = (bid) => {
    return bid?._id || bid?.id;
  };
  
  // Helper function to check if there are any active bids
  const hasActiveBids = (bids) => {
    return bids.some(bid => bid.status === 'pending');
  };

  //helper function for different rate types
  const formatRateTypesDisplay = (request) => {
  
  // PRIORITÀ 1: Controlla il nuovo array rateTypes
  if (request.rateTypes && Array.isArray(request.rateTypes) && request.rateTypes.length > 0) {
    const formatted = request.rateTypes.map(type => 
      type === 'fixed' ? t('requests.fixed') : t('requests.floating')
    ).join(', ');
    return formatted;
  }
  
  // PRIORITÀ 2: Fallback al vecchio campo rateType
  if (request.rateType) {
    const formatted = request.rateType === 'fixed' ? t('requests.fixed') : t('requests.floating');
    return formatted;
  }
  
  // PRIORITÀ 3: Fallback finale
  return 'N/A';
};

/* TODO: Commissioni - da abilitare in futuro (usato dal modale di conferma)
const formatBaseRate = (baseRate) => {
  return formatBaseRateDisplay(baseRate, t);
};
*/

const getBenchmarkForDuration = (duration) => {
  if (!duration) return null;
  if (['overnight', '7d', '14d', '21d', 'custom'].includes(duration)) return 'ESTR';
  if (['1', '2'].includes(duration)) return 'EURIBOR 1M';
  if (duration === '3') return 'EURIBOR 3M';
  if (duration === '6') return 'EURIBOR 6M';
  return null;
};

const getBenchmarkField = (duration) => {
  if (!duration) return null;
  if (['overnight', '7d', '14d', '21d', 'custom'].includes(duration)) return 'estrON';
  if (['1', '2'].includes(duration)) return 'euribor1M';
  if (duration === '3') return 'euribor3M';
  if (duration === '6') return 'euribor6M';
  return null;
};

// Interessi dell'offerta: sull'importo dell'offerta, in allocazione sull'importo inserito dall'azienda,
// ad allocazione confermata sull'importo effettivamente allocato
const getBidInterest = (bid) => {
  const amount = request.status === 'pending_allocation'
    ? allocations[getBidId(bid)]?.amount || 0
    : request.status === 'completed'
    ? (bid.status === 'approved' ? bid.allocatedAmount || 0 : 0)
    : bid.amount;
  return computeDepositInterest(amount, bid.effectiveRate, getBidDayCount(request, bid));
};

// Totale interessi sulle allocazioni (null se la durata non permette il calcolo)
const getTotalInterest = () => {
  const interests = bids.filter(bid => bid.status !== 'cancelled').map(getBidInterest);
  if (interests.some(interest => interest === null)) return null;
  return Math.round(interests.reduce((sum, interest) => sum + interest, 0) * 100) / 100;
};

// Colonne della tabella offerte: 6 fisse, +1 con l'input di allocazione, +2 (importo allocato, allocato da) a richiesta completata
const depositColumnCount = 6
  + (request?.status === 'pending_allocation' ? 1 : 0)
  + (request?.status === 'completed' ? 2 : 0);

// Somma degli importi effettivamente allocati alle offerte (richiesta completata)
const getTotalAllocatedAmount = () => bids
  .filter(bid => bid.status === 'approved')
  .reduce((sum, bid) => sum + (bid.allocatedAmount || 0), 0);

// Interessi formattati per la tabella: "—" per le offerte non allocate a richiesta completata
const formatBidInterest = (bid, interest) => {
  if (request.status === 'completed' && bid.status !== 'approved') return '—';
  return interest !== null ? formatCostAmount(interest, request.currency || 'EUR') : t('bids.notAvailable');
};

const interestLabel = () => request.requestType === 'hot_money'
  ? t('requestDetail.interest.passive')
  : t('requestDetail.interest.active');

// Render bids come mobile cards (riusa struttura esistente)
const renderMobileBidCard = (bid) => {
  const bidId = getBidId(bid);
  if (!bid || !bidId || !bid.bank) return null;

  const isCancelled = bid.status === 'cancelled';
  const interest = getBidInterest(bid);

  return (
    <div key={bidId} className={`mobile-record-card ${isCancelled ? 'cancelled' : ''}`}>
      <div className="mobile-card-header">
        <div className="mobile-card-title">
          {!isAffiliatedBank(bid.bank._id || bid.bank.id) ? (
            <Link 
              to={`/company/bank-info/${encodeURIComponent(bid.bank.name)}`}
              className="bank-info-link"
              title={t('bankDetails.viewBankInfo')}
            >
              {bid.bank.name}
            </Link>
          ) : (
            bid.bank.name
          )}
        </div>
        <div className="mobile-card-status" style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {isBankRenewal(bid.bank._id || bid.bank.id) && (
            <span className="status" style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' }}>
              {t('requests.renewal')}
            </span>
          )}
          <span className={`status status-${bid.status}`}>
            {t(`status.${bid.status}`)}
          </span>
        </div>
      </div>
      
      <div className="mobile-card-body">
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('bids.interestRate')}</span>
          <span className="mobile-card-value">
            <span className="interest-rate">
              {formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}
            </span>
          </span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.dayCountBasisShort')}</span>
          <span className="mobile-card-value">{formatDayCountBasis(withDayCountBasis(bid), t)}</span>
        </div>

        {/* Colonna Tipo di tasso - disabilitata temporaneamente
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.rateType')}</span>
          <span className="mobile-card-value">
            <span className={`rate-type-badge ${bid.rateType}`}>
              {bid.rateType === 'fixed' ? t('requests.fixed') : t('requests.floating')}
            </span>
          </span>
        </div>
        */}

        {/* Riga fittizia solo frontend per demo - disabilitata, lasciata per eventuali demo future
        <div className="mobile-card-row">
          <span className="mobile-card-label">Base calcolo</span>
          <span className="mobile-card-value">365</span>
        </div>
        */}

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('bids.bidAmount')}</span>
          <span className="mobile-card-value">
            {formatCurrencyWithSymbol(bid.amount, request.currency || 'EUR')}
          </span>
        </div>
        
        {request.status === 'pending_allocation' && allocations[bidId] && (
          <div className="mobile-card-row" style={{flexDirection: 'column', alignItems: 'stretch'}}>
            <span className="mobile-card-label" style={{marginBottom: '8px'}}>
              {t('allocation.yourAllocation')}
            </span>
            <input
              type="text"
              className="mobile-allocation-input"
              value={editingInputId === bidId
                ? editingInputValue
                : formatCurrencyWithSymbol(allocations[bidId].amount, request.currency || 'EUR')}
              onChange={(e) => !isCancelled && handleAllocationChange(bidId, e.target.value)}
              onFocus={() => !isCancelled && handleInputFocus(bidId)}
              onBlur={handleInputBlur}
              disabled={processingAllocation || isCancelled}
              placeholder={t('allocation.enterAmount')}
            />
          </div>
        )}

        {request.status === 'completed' && (
          <>
            <div className="mobile-card-row">
              <span className="mobile-card-label">{t('bids.allocatedAmount')}</span>
              <span className={`mobile-card-value ${bid.status === 'approved' ? 'amount-display allocated' : ''}`}>
                {bid.status === 'approved' ? formatCurrencyWithSymbol(bid.allocatedAmount, request.currency || 'EUR') : '—'}
              </span>
            </div>
            <div className="mobile-card-row">
              <span className="mobile-card-label">{t('requests.allocatedBy')}</span>
              <span className="mobile-card-value">
                {bid.status === 'approved' && bid.allocatedBy ? `${bid.allocatedBy.firstName} ${bid.allocatedBy.lastName}` : '—'}
              </span>
            </div>
          </>
        )}

        <div className="mobile-card-row dep-interest-cell">
          <span className="mobile-card-label">{interestLabel()}</span>
          <span className="mobile-card-value dep-interest-total">
            {formatBidInterest(bid, interest)}
          </span>
        </div>
      </div>

      {(bid.notes || bid.noticePeriod || bid.interestAvailableForRolling > 0) && (
        <div className="mobile-bid-extra">
          {bid.noticePeriod && (
            <div className="detail-item-modern">
              <div className="detail-label">{t('requestDetail.bidDetails.noticePeriod')}</div>
              <div className="detail-value">{bid.noticePeriod} {t('common.days')}</div>
            </div>
          )}
          {bid.interestAvailableForRolling > 0 && (
            <div className="detail-item-modern">
              <div className="detail-label">{t('bids.interestAvailableForRolling')}</div>
              <div className="detail-value">{formatCurrencyWithSymbol(bid.interestAvailableForRolling, request.currency || 'EUR')}</div>
            </div>
          )}
          {bid.notes && (
            <div className="detail-item-modern">
              <div className="detail-label">{t('requestDetail.bidDetails.bankNotes')}</div>
              <div className="detail-value">{bid.notes}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
  
  // Helper function to get localized error messages
  const getLocalizedErrorMessage = (errorKey, params = {}) => {
    const errorMessage = t(errorKey, params);
    
    if (errorMessage === errorKey) {
      switch (errorKey) {
        case 'allocation.errors.exceedsLiquidity':
          return i18n.language === 'it' 
            ? 'L\'allocazione totale supera la liquidità disponibile!' 
            : 'Total allocation exceeds available liquidity!';
        case 'allocation.errors.noAllocation':
          return i18n.language === 'it'
            ? 'Devi allocare fondi ad almeno un\'offerta!'
            : 'You must allocate funds to at least one bid!';
        case 'allocation.errors.noActiveBids':
          return i18n.language === 'it'
            ? 'Devi allocare fondi ad almeno un\'offerta attiva!'
            : 'You must allocate funds to at least one active bid!';
      case 'allocation.errors.exceedsBidAmount':
        return i18n.language === 'it'
          ? `L'allocazione per ${params.bankName} (${formatCurrencyWithSymbol(params.allocation, params.currency)}) supera l'importo della loro offerta (${formatCurrencyWithSymbol(params.bidAmount, params.currency)})`
          : `Allocation for ${params.bankName} (${formatCurrencyWithSymbol(params.allocation, params.currency)}) exceeds their bid amount (${formatCurrencyWithSymbol(params.bidAmount, params.currency)})`;
        default:
          return errorMessage;
      }
    }
    
    return errorMessage;
  };
  
// Helper function to check if a bank has the renewal flag set
const isBankRenewal = (bankOrgId) => {
  if (!request?.bankRenewalFlags || !bankOrgId) return false;
  return request.bankRenewalFlags.some(f =>
    f.bankOrganization?.toString() === bankOrgId?.toString() && f.isRenewal === true
  );
};

// Helper function to check if a bank is affiliated
const isAffiliatedBank = (bankOrgId) => {
  if (!affiliatedBanks || affiliatedBanks.length === 0) {
    return false;
  }
  
  return affiliatedBanks.some(ab => {
    const isAffiliated = 
      ab.organizationId?.toString() === bankOrgId?.toString() || 
      ab.organizationId?._id?.toString() === bankOrgId?.toString() || 
      ab.userId?.toString() === bankOrgId?.toString() || 
      ab.userId?._id?.toString() === bankOrgId?.toString();
    
    if (isAffiliated) {
    }
    
    return isAffiliated;
  });
};

// SOSTITUISCI la funzione getSelectedBankNames() esistente con questa versione corretta:

const getSelectedBankNames = () => {
  if (!request || (request.targetBanks !== 'affiliated' && request.targetBanks !== 'custom') || !request.selectedBanks || request.selectedBanks.length === 0) {
    return [];
  }
  
  if (!affiliatedBanksLoaded) {
  return [];
}
  
  const bankNames = request.selectedBanks.map(selectedBankId => {
  // Cerca prima negli affiliatedBanks
  const affiliatedBank = affiliatedBanks.find(ab => 
    ab.userId?.toString() === selectedBankId?.toString() || 
    ab._id?.toString() === selectedBankId?.toString()
  );
  
  if (affiliatedBank) {
    if (affiliatedBank.organizationId && affiliatedBank.organizationId.name) {
      return affiliatedBank.organizationId.name;
    }
    if (affiliatedBank.referralDetails && affiliatedBank.referralDetails.bankName) {
      return affiliatedBank.referralDetails.bankName;
    }
    if (affiliatedBank.bankName) {
      return affiliatedBank.bankName;
    }
    if (affiliatedBank.organizationName) {
      return affiliatedBank.organizationName;
    }
    if (affiliatedBank.firstName && affiliatedBank.lastName) {
      return `${affiliatedBank.firstName} ${affiliatedBank.lastName}`;
    }
  }
  
  // Cerca nelle thirdPartyBanks
  const thirdPartyBank = thirdPartyBanks.find(tp => 
    tp._id?.toString() === selectedBankId?.toString()
  );
  
  if (thirdPartyBank) {
    if (thirdPartyBank.organization?.name) {
      return thirdPartyBank.organization.name;
    }
    if (thirdPartyBank.firstName && thirdPartyBank.lastName) {
      return `${thirdPartyBank.firstName} ${thirdPartyBank.lastName}`;
    }
  }
  
  return 'Nome banca non disponibile';
});
  
  // ✅ RIMUOVI DUPLICATI: Usa Set per eliminare nomi duplicati
  const uniqueBankNames = [...new Set(bankNames.filter(name => name !== 'Nome banca non disponibile'))];

  return uniqueBankNames;
};

 // Helper function to get localized server error messages
  const getLocalizedServerError = (serverMessage) => {
    if (typeof serverMessage !== 'string') return serverMessage;
    
    const lowerMessage = serverMessage.toLowerCase();
    
    if (i18n.language === 'it') {
      if (lowerMessage.includes('exceeds available liquidity') || lowerMessage.includes('supera la liquidità')) {
        return 'L\'allocazione totale supera la liquidità disponibile';
      }
      if (lowerMessage.includes('no allocation') || lowerMessage.includes('must allocate')) {
        return 'Devi allocare fondi ad almeno un\'offerta';
      }
      if (lowerMessage.includes('exceeds bid amount') || lowerMessage.includes('supera l\'importo dell\'offerta')) {
        return 'L\'allocazione supera l\'importo dell\'offerta';
      }
      
      if (lowerMessage.includes('below minimum required allocation')) {
        const amountPattern = /[€$£][Ì†\d,.]+/g;
        const amounts = serverMessage.match(amountPattern);
        if (amounts && amounts.length >= 2) {
          const allocatedAmount = amounts[0];
          const minimumAmount = amounts[1];
          return `${allocatedAmount} è sotto l'allocazione minima richiesta ${minimumAmount}`;
        }
        return 'L\'allocazione è sotto il minimo richiesto';
      }
      
      if (lowerMessage.includes('invalid allocation') || lowerMessage.includes('allocazione non valida')) {
        return 'Allocazione non valida';
      }
      if (lowerMessage.includes('bid not found') || lowerMessage.includes('offerta non trovata')) {
        return 'Offerta non trovata';
      }
      if (lowerMessage.includes('request not found') || lowerMessage.includes('richiesta non trovata')) {
        return 'Richiesta non trovata';
      }
    }
    
    return serverMessage;
  };
  
  useEffect(() => {
    const fetchRequestDetails = async () => {
      try {
        const res = await axios.get(`/api/requests/${id}`);
        
        setRequest(res.data.request);
        
        // Sort bids: hot_money = lowest rate first (cost to pay), others = highest rate first
        const isHotMoney = res.data.request.requestType === 'hot_money';
        const sortedBids = [...res.data.bids].sort((a, b) =>
          isHotMoney ? a.interestRate - b.interestRate : b.interestRate - a.interestRate
        );
        setBids(sortedBids);       
               
        // Initialize allocations for pending allocation requests
        if (res.data.request.status === 'pending_allocation') {
          const initialAllocations = {};
          
          sortedBids.forEach(bid => {
            const bidId = getBidId(bid);
            
            if (!bidId) {
              console.error("Invalid bid object (no ID found)");
              return;
            }
            
            initialAllocations[bidId] = {
              amount: 0
            };
          });
          
          setAllocations(initialAllocations);
          setTotalAllocated(0);
        }
        
        setLoading(false);

        // Fetch affiliated banks for relationship display
        const fetchAffiliatedBanks = async () => {
          try {
            const response = await axios.get('/api/company/bank-referrals');

            if (response.data) {
              const banks = response.data.affiliatedBanks || response.data.bankReferrals || [];

              setAffiliatedBanks(banks);
            }
          } catch (err) {
            console.error('Error fetching affiliated banks:');
          } finally {
            setAffiliatedBanksLoaded(true);
          }
        };
        
        // Fetch third party banks
        const fetchThirdPartyBanks = async () => {
          try {
            const response = await axios.get('/api/requests/third-party-banks');
            if (response.data) {
              setThirdPartyBanks(response.data.thirdPartyBanks || []);
            }
          } catch (err) {
            console.error('Error fetching third party banks:', err);
          }
        };
        
        fetchAffiliatedBanks();
        fetchThirdPartyBanks();

        // Fetch benchmark rate for EUR requests
        const currency = res.data.request.currency || 'EUR';
        const duration = res.data.request.duration;
        const field = getBenchmarkField(duration);
        if (currency === 'EUR' && field) {
          // While the request is open, always look up the most recent benchmark
          // available as of today (the by-date endpoint falls back to the closest
          // earlier date if today's rate hasn't been published yet).
          // Once the request is allocated or cancelled, freeze the lookup on the
          // allocation date (or the bid deadline if nothing was allocated), so later
          // benchmarks are never shown for a past auction.
          const { status, allocationDate, bidDeadline } = res.data.request;
          const isFinalized = ['pending_transfer', 'completed', 'cancelled'].includes(status);
          const frozenDate = allocationDate || bidDeadline;
          const referenceDate = isFinalized && frozenDate
            ? new Date(frozenDate)
            : new Date();
          const requestDate = referenceDate.toISOString().split('T')[0];
          try {
            const rateRes = await axios.get(`/api/markets/rates/by-date?date=${requestDate}`);
            const value = rateRes.data[field];
            setBenchmarkRate(value !== undefined ? value : null);
            const actualDate = new Date(rateRes.data.date).toISOString().split('T')[0];
            const [y, m, d] = actualDate.split('-');
            setBenchmarkDate(`${d}/${m}/${y}`);
          } catch {
            setBenchmarkRate(null);
          }
        }

        /* TODO: Commissioni - da abilitare in futuro
        // Fetch commission info for non-paying companies
        const fetchCommissionInfo = async () => {
          try {
            const response = await axios.get(`/api/requests/${id}/commission-info`);
            if (response.data) {
              setCommissionInfo(response.data);
            }
          } catch (err) {
            console.error('Error fetching commission info:', err);
          }
        };

        fetchCommissionInfo();
        */

      } catch (err) {
        setError(t('common.errors.fetchDetails'));
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [id, t]);
  
  const handleAllocationChange = (bidId, value) => {
    if (!allocations[bidId]) {
      console.error("Tried to update allocation for non-existent bid");
      return;
    }

    // Keep the formatted string for display during editing
    const rawValue = value;
    let formatted;
    if (rawValue.endsWith('.') || rawValue.endsWith(',')) {
      formatted = formatCurrencyInput(rawValue.slice(0, -1));
      formatted = formatted ? formatted + '.' : '';
    } else {
      formatted = formatCurrencyInput(rawValue);
    }
    setEditingInputValue(formatted);

    // Parse the numeric value for calculations
    const amount = parseCurrencyInput(value);

    const updatedAllocations = {
      ...allocations,
      [bidId]: {
        ...allocations[bidId],
        amount
      }
    };

    setAllocations(updatedAllocations);

    const total = Object.values(updatedAllocations).reduce(
      (sum, alloc) => sum + alloc.amount,
      0
    );

    setTotalAllocated(total);
    setAllocationError('');
  };
  
  const handleInputFocus = (bidId) => {
    setEditingInputId(bidId);
    // Initialize the editing string from the current numeric value
    if (allocations[bidId]) {
      setEditingInputValue(formatCurrencyInput(allocations[bidId].amount));
    }
  };
  
  const handleInputBlur = () => {
    setEditingInputId(null);
  };
  
  // Helper to prepare bid allocations array
  const prepareBidAllocations = () => {
    const bidAllocations = [];

    for (const bid of bids) {
      if (!bid) continue;

      const bidId = getBidId(bid);
      if (!bidId) continue;

      const allocation = allocations[bidId];
      if (!allocation) continue;

      if (bid.status === 'cancelled') continue;

      if (allocation.amount > 0) {
        bidAllocations.push({
          bidId: bidId,
          amount: allocation.amount,
          interestRate: bid.interestRate,
          bankName: bid.bank?.name
        });
      }
    }

    return bidAllocations;
  };

  const confirmAllocation = async () => {
    try {
      const hasBidsWithAllocation = Object.values(allocations).some(allocation =>
        allocation.amount > 0
      );

      if (!hasBidsWithAllocation) {
        return setAllocationError(getLocalizedErrorMessage('allocation.errors.noAllocation'));
      }

      setAllocationError('');

      /* TODO: Commissioni - da abilitare in futuro
      // Show confirmation modal for non-paying companies
      if (commissionInfo && commissionInfo.isPaying === false) {
        // Call the server API to get commission preview with correct calculations
        const bidAllocations = prepareBidAllocations().map(({ bidId, amount }) => ({ bidId, amount }));
        const previewResponse = await axios.post(`/api/requests/${id}/commission-preview`, {
          allocations: bidAllocations
        });
        setCommissionPreviewData(previewResponse.data);
        setShowConfirmModal(true);
        return;
      }
      */

      // Proceed directly with allocation
      await executeAllocation();
    } catch (err) {
      setProcessingAllocation(false);

      if (err.response?.status === 400 && err.response?.data?.message) {
        const localizedMessage = getLocalizedServerError(err.response.data.message);
        setAllocationError(localizedMessage);
      } else if (err.response?.status === 404) {
        const notFoundMessage = i18n.language === 'it'
          ? 'Risorsa non trovata'
          : 'Resource not found';
        setError(notFoundMessage);
      } else {
        const fallbackMessage = i18n.language === 'it'
          ? 'Impossibile confermare l\'allocazione. Contatta l\'amministratore.'
          : 'Unable to confirm the allocation. Contact the administrator.';
        setError(fallbackMessage);
      }
    }
  };

  const executeAllocation = async () => {
    try {
      setProcessingAllocation(true);
      setError('');
      setAllocationError('');
      // setShowConfirmModal(false); // TODO: Commissioni - da abilitare in futuro

      const bidAllocations = prepareBidAllocations().map(({ bidId, amount }) => ({ bidId, amount }));

      if (bidAllocations.length === 0) {
        setProcessingAllocation(false);
        return setAllocationError(getLocalizedErrorMessage('allocation.errors.noActiveBids'));
      }

      await axios.post('/api/bids/allocate', {
        requestId: request.id || request._id,
        allocations: bidAllocations
      });

      setAllocationCompleted(true);

      setTimeout(() => {
        navigate(request?.requestType === 'hot_money' ? '/company/past-requests/hot-money' : '/company/past-requests/allocation', { replace: true });
      }, 2500);
      
    } catch (err) {          
      setProcessingAllocation(false);
      
      if (err.response?.status === 400 && err.response?.data?.message) {
        const localizedMessage = getLocalizedServerError(err.response.data.message);
        setAllocationError(localizedMessage);
      } else if (err.response?.status === 404) {
        const notFoundMessage = i18n.language === 'it' 
          ? 'Risorsa non trovata' 
          : 'Resource not found';
        setError(notFoundMessage);
      } else {
        const fallbackMessage = i18n.language === 'it' 
          ? 'Impossibile confermare l\'allocazione. Contatta l\'amministratore.' 
          : 'Unable to confirm the allocation. Contact the administrator.';
        setError(fallbackMessage);
      }
    }
  };
  
  const updateRequestStatus = async (newStatus) => {
    if (newStatus === 'cancelled') {
      const confirmMessage = i18n.language === 'it' 
        ? 'Sei sicuro di voler annullare questa richiesta? Questa azione non può essere annullata.'
        : 'Are you sure you want to cancel this request? This action cannot be undone.';
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }
    
    try {
      setProcessingAction(true);
      setError('');
      
      await axios.put(`/api/requests/${id}/status`, { status: newStatus });
      
      if (newStatus === 'cancelled') {
        navigate(request?.requestType === 'hot_money' ? '/company/past-requests/hot-money' : '/company/past-requests/allocation');
      } else if (newStatus === 'pending_allocation') {
        const res = await axios.get(`/api/requests/${id}`);
        setRequest(res.data.request);
        
        const sortedBids = [...res.data.bids].sort((a, b) =>
          request?.requestType === 'hot_money' ? a.interestRate - b.interestRate : b.interestRate - a.interestRate
        );
        setBids(sortedBids);

        const initialAllocations = {};
        
        sortedBids.forEach(bid => {
          const bidId = getBidId(bid);
          
          if (!bidId) {
            console.error("Invalid bid object (no ID found)");
            return;
          }
                    
          initialAllocations[bidId] = {
            amount: 0
          };
        });
               
        setAllocations(initialAllocations);
        setTotalAllocated(0);
      }
      
      setProcessingAction(false);
    } catch (err) {
      console.error("Error updating request status:");
      
      let errorMessage = t('common.errors.updateStatus');
      if (err.response?.data?.message) {
        errorMessage = getLocalizedServerError(err.response.data.message);
      }
      
      setError(errorMessage);
      setProcessingAction(false);
    }
  };
  
  if (loading) {
    return <div className="loading">{t('common.loadingRequests')}</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  if (!request) {
    const notFoundMessage = i18n.language === 'it' ? 'Richiesta non trovata' : 'Request not found';
    return <div className="error-message">{notFoundMessage}</div>;
  }
  
  if (allocationCompleted) {
    return (
      <div className="card">
        <div className="alert-success" style={{ padding: '20px', margin: '20px 0' }}>
          <h3>{t('requestDetail.messages.allocationCompletedSuccess')}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="request-detail company-dashboard">
      <div className="page-navigation" style={{ marginBottom: '14px' }}>
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate(backToDashboard || (request?.requestType === 'hot_money' ? '/company/past-requests/hot-money' : '/company/past-requests/allocation'))}
          disabled={processingAction}
        >
         {backToDashboard ? t('requestDetail.navigation.backToDashboard') : t('requestDetail.navigation.backToRequests')}
        </button>
      </div>

      <div className="card">
        <div className="card-title">
          <span>
            <Info className="card-title-icon" size={20}/>
            {t('requests.requestDetails')}
          </span>
          <div>
            <span className={`status status-${request.status}`}>
              {t(`status.${request.status}`)}
            </span>
          </div>
        </div>
        
        <div className="request-info">
          <h3 style={request.requestType === 'hot_money' ? { color: '#e65100' } : {}}>
            {request.requestType === 'hot_money'
              ? t('requestDetail.info.hotMoneyRequestInformation')
              : t('requestDetail.info.requestInformation')}
            <span className="request-info-subtitle">
              {t('requestDetail.info.requestMeta', {
                id: request.requestId,
                date: new Date(request.createdAt).toLocaleDateString(),
                name: request.createdBy
                  ? `${request.createdBy.firstName} ${request.createdBy.lastName}`
                  : t('bids.notAvailable')
              })}
            </span>
          </h3>

          {/* New grid layout for request information */}
          <div className="info-grid info-list">
            <div className="info-card amount">
              <div className="info-card-label">{t('requests.amount')}</div>
              <div className="info-card-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</div>
            </div>

            <div className="info-card">
              <div className="info-card-label">{t('requests.maturity')}</div>
              <div className="info-card-value">{formatDuration(request.duration, t, request.customStartDate, request.customEndDate)}</div>
            </div>

            {(request.targetBanks === 'affiliated' || request.targetBanks === 'custom') && request.selectedBanks && request.selectedBanks.length > 0 && (
              <div className="info-card">
                <div className="info-card-label">{t('requests.selectedBanks') || 'Banche Selezionate'}</div>
                <div className="info-card-value">
                  {!affiliatedBanksLoaded ? (
                    <span style={{ color: '#6c757d' }}>Caricamento...</span>
                  ) : (
                    (() => {
                      const bankNames = getSelectedBankNames();
                      return bankNames.length > 0 ? (
                        <span style={{ display: 'inline-block', lineHeight: '1.4' }}>
                          {bankNames.join(', ')}
                        </span>
                      ) : (
                        <span style={{ color: '#6c757d' }}>
                          {request.selectedBanks.length} {t('requests.banksSelected') || 'bank(s) selected'}
                        </span>
                      );
                    })()
                  )}
                </div>
              </div>
            )}

            {request.company?.isPaying !== false && (
              <div className="info-card">
                <div className="info-card-label">{t('requests.acceptedRateTypes')}</div>
                <div className="info-card-value">{formatRateTypesDisplay(request)}</div>
              </div>
            )}

            {request.requestType !== 'hot_money' && (
              <div className="info-card">
                <div className="info-card-label">{t('requestDetail.info.withdrawalType')}</div>
                <div className="info-card-value">
                  {request.withdrawalType === 'demand' && t('requestDetail.withdrawal.demand')}
                  {request.withdrawalType === 'early_allowed' && t('requestDetail.withdrawal.earlyAllowed')}
                  {request.withdrawalType === 'no_withdrawal' && t('requestDetail.withdrawal.noWithdrawal')}
                </div>
              </div>
            )}

            <div className="info-card">
              <div className="info-card-label">{t('requests.bidDeadline')}</div>
              <div className="info-card-value">
                {new Date(request.bidDeadline).toLocaleDateString()}
                {' '}
                <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
                  {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} CET
                </span>
              </div>
            </div>

            {request.notes && (
              <div className="info-card notes-section">
                <div className="info-card-label">{t('requestDetail.info.notes')}</div>
                <div className="info-card-value">{request.notes}</div>
              </div>
            )}
          </div>
        </div>
      
      <div className="bids-section">
        <h3>
          {request.status === 'pending_allocation' 
            ? t('requestDetail.bidsSection.allocationManagement')
            : request.status === 'completed'
            ? t('requestDetail.bidsSection.finalAllocation')
            : request.status === 'cancelled' 
            ? t('requestDetail.bidsSection.cancelledRequest')
            : t('requestDetail.bidsSection.bidsReceived')}
        </h3>
        
        {bids.length > 0 ? (
           <>
          {/* Desktop Table View */}
          <div className="bids-table-container dep-bids-table-container">
            <table className={`bids-table deposit-bids-table ${request.status === 'pending_allocation' ? 'with-allocation' : ''} ${request.status === 'completed' ? 'with-allocated' : ''}`}>
              <colgroup>
                <col className="dep-col-bank" />
                <col className="dep-col-rate" />
                <col className="dep-col-day-count" />
                <col className="dep-col-amount" />
                {request.status !== 'pending_allocation' && <col className="dep-col-status" />}
                {request.status === 'pending_allocation' && <col className="dep-col-allocation" />}
                {request.status === 'pending_allocation' && <col className="dep-col-status" />}
                {request.status === 'completed' && <col className="dep-col-allocated" />}
                {request.status === 'completed' && <col className="dep-col-allocated-by" />}
                <col className="dep-col-interest" />
              </colgroup>
              <thead>
                <tr>
                  <th>{t('bids.bank')}</th>
                  <th>{t('bids.interestRate')}</th>
                  <th>{t('vatCredit.dayCountBasisShort')}</th>
                  {/* Colonna Tipo di tasso - disabilitata temporaneamente
                  <th>{t('requests.rateType')}</th>
                  */}
                  {/* Colonna fittizia solo frontend per demo - disabilitata, lasciata per eventuali demo future
                  <th>Base calcolo</th>
                  */}
                  <th>{t('bids.bidAmount')}</th>
                  {/* TODO: Commissioni - da abilitare in futuro
                  {commissionInfo && commissionInfo.isPaying === false && (
                    <th>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {t('commission.managementCost') || 'Costo Gestione'}
                        <span
                          className="info-tooltip"
                          title={request.duration === 'overnight'
                            ? (t('commission.overnightTooltip') || 'Per i conti correnti è una commissione fissa di 100€')
                            : (t('commission.depositTooltip') || 'Per i conti deposito è una % progressiva dell\'importo allocato')}
                          style={{ cursor: 'help', color: '#6c757d' }}
                        >
                          <Info size={14} />
                        </span>
                      </span>
                    </th>
                  )}
                  */}
                  {/* Stato prima delle colonne di allocazione, tranne durante l'allocazione (dopo l'input) */}
                  {request.status !== 'pending_allocation' && <th>{t('bids.status')}</th>}
                  {request.status === 'pending_allocation' && <th>{t('allocation.yourAllocation')}</th>}
                  {request.status === 'pending_allocation' && <th>{t('bids.status')}</th>}
                  {request.status === 'completed' && <th>{t('bids.allocatedAmount')}</th>}
                  {request.status === 'completed' && <th>{t('requests.allocatedBy')}</th>}
                  <th className="dep-interest-cell dep-interest-start" title={t('requestDetail.interest.formula')}>{interestLabel()}</th>
                </tr>
              </thead>
              <tbody>
                {bids.map(bid => {
                  const bidId = getBidId(bid);

                  if (!bid || !bidId || !bid.bank) {
                    console.error("Invalid bid object in render");
                    return null;
                  }

                  const isCancelled = bid.status === 'cancelled';
                  const interest = getBidInterest(bid);
                  const statusCell = (
                    <td>
                      <span className={`status status-${bid.status}`}>
                        {t(`status.${bid.status}`)}
                      </span>
                    </td>
                  );

                  return (
                    <React.Fragment key={bidId}>
                      <tr className={isCancelled ? 'cancelled-bid' : ''}>
                        <td>
                          <div className="bank-name">
                            {!isAffiliatedBank(bid.bank._id || bid.bank.id) ? (
                              <Link
                                to={`/company/bank-info/${encodeURIComponent(bid.bank.name)}`}
                                className="bank-info-link"
                                title={t('bankDetails.viewBankInfo')}
                              >
                                {bid.bank.name}
                              </Link>
                            ) : (
                              bid.bank.name
                            )}
                            {isBankRenewal(bid.bank._id || bid.bank.id) && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  marginLeft: '6px',
                                  padding: '1px 6px',
                                  fontSize: '11px',
                                  borderRadius: '4px',
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  border: '1px solid #a5d6a7',
                                  verticalAlign: 'middle'
                                }}
                              >
                                {t('requests.renewal')}
                              </span>
                            )}
                          </div>
                          <div className="bid-submitted-at">
                            {t('bids.submittedAt', { date: formatSubmittedAt(bid.createdAt) })}
                          </div>
                        </td>
                        <td>
                          <div className="interest-rate">
                            {formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}
                          </div>
                        </td>
                        <td>{formatDayCountBasis(withDayCountBasis(bid), t)}</td>
                        {/* Colonna Tipo di tasso - disabilitata temporaneamente
                        <td>
                          <span className={`rate-type-badge ${bid.rateType}`}>
                            {bid.rateType === 'fixed' ? t('requests.fixed') : t('requests.floating')}
                          </span>
                        </td>
                        */}
                        {/* Valore fittizio solo frontend per demo - disabilitata, lasciata per eventuali demo future
                        <td>365</td>
                        */}
                        <td>
                          <div className="amount-display primary">
                            {formatCurrencyWithSymbol(bid.amount, request.currency || 'EUR')}
                          </div>
                        </td>
                        {/* TODO: Commissioni - da abilitare in futuro
                        {commissionInfo && commissionInfo.isPaying === false && (
                          <td>
                            <div className="commission-rate-display">
                              {getCommissionRateDisplay(request.amount, request.duration)}
                            </div>
                          </td>
                        )}
                        */}
                        {request.status !== 'pending_allocation' && statusCell}
                        {request.status === 'pending_allocation' && allocations[bidId] && (
                          <td>
                            <div className="allocation-input-container">
                              <input
                                type="text"
                                className={`allocation-input ${isCancelled ? 'disabled' : ''}`}
                                value={editingInputId === bidId
                                ? editingInputValue
                                : formatCurrencyWithSymbol(allocations[bidId].amount, request.currency || 'EUR')}
                                onChange={(e) => !isCancelled && handleAllocationChange(bidId, e.target.value)}
                                onFocus={() => !isCancelled && handleInputFocus(bidId)}
                                onBlur={handleInputBlur}
                                disabled={processingAllocation || isCancelled}
                                placeholder={t('allocation.enterAmount')}
                              />
                              {isCancelled && (
                                <div className="cancelled-bid-message">
                                  {t('requestDetail.messages.cancelledBidMessage')}
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                        {request.status === 'pending_allocation' && statusCell}
                        {request.status === 'completed' && (
                          <td>
                            <div className={`amount-display primary ${bid.status === 'approved' ? 'allocated' : ''}`}>
                              {bid.status === 'approved' ? formatCurrencyWithSymbol(bid.allocatedAmount, request.currency || 'EUR') : '—'}
                            </div>
                          </td>
                        )}
                        {request.status === 'completed' && (
                          <td>
                            {bid.status === 'approved' && bid.allocatedBy ? `${bid.allocatedBy.firstName} ${bid.allocatedBy.lastName}` : '—'}
                          </td>
                        )}
                        <td className="dep-interest-cell dep-interest-start dep-interest-total">
                          {formatBidInterest(bid, interest)}
                        </td>
                      </tr>
                      
                      {/* Additional details row - shown for bids with extra information */}
                        {(bid.notes ||
                        (request.withdrawalType === 'early_allowed' && bid.noticePeriod) ||
                        (request.withdrawalType === 'early_allowed' && typeof bid.penaltyApplies !== 'undefined') ||
                        (!isAffiliatedBank(bid.bank._id || bid.bank.id) && typeof bid.supportAccount !== 'undefined') ||
                        bid.interestAvailableForRolling > 0) && (
                        <tr className="bid-details-row">
                          <td colSpan={depositColumnCount}>
                            <div className="bid-details-content">
                              <div className="details-grid">
                                {request.withdrawalType === 'early_allowed' && bid.noticePeriod && (
                                  <div className="detail-item-modern">
                                    <div className="detail-label">{t('requestDetail.bidDetails.noticePeriod')}</div>
                                    <div className="detail-value">{bid.noticePeriod} {t('common.days')}</div>
                                  </div>
                                )}

                                {/*request.duration === 'overnight' && bid.lockInPeriod && (
                                  <div className="detail-item-modern">
                                    <div className="detail-label">{t('requestDetail.bidDetails.lockInPeriod')}</div>
                                    <div className="detail-value">{new Date(bid.lockInPeriod).toLocaleDateString()}</div>
                                  </div>
                                )*/}

                                {request.withdrawalType === 'early_allowed' && typeof bid.penaltyApplies !== 'undefined' && (
                                  <>
                                    <div className="detail-item-modern">
                                      <div className="detail-label">{t('requestDetail.bidDetails.withdrawalPenalty')}</div>
                                      <div className="detail-value">
                                        {bid.penaltyApplies ? t('common.yes') : t('common.no')}
                                      </div>
                                    </div>
                                    {bid.penaltyApplies && bid.penaltyDescription && (
                                      <div className="detail-item-modern">
                                        <div className="detail-label">{t('requestDetail.bidDetails.penaltyTerms')}</div>
                                        <div className="detail-value">{bid.penaltyDescription}</div>
                                      </div>
                                    )}
                                  </>
                                )}
                                
                                {/* Support Account - mostrato solo per banche NON affiliate */}
                                {!isAffiliatedBank(bid.bank._id || bid.bank.id) && typeof bid.supportAccount !== 'undefined' && (
                                  <div className="detail-item-modern" style={{ maxWidth: '350px' }}>
                                    <div className="detail-label">{t('bids.supportAccount')}</div>
                                    <div className="detail-value">{bid.supportAccount ? t('common.yes') : t('common.no')}</div>
                                  </div>
                                )}

                                {/* Interest Available for Rolling */}
                                {bid.interestAvailableForRolling > 0 && (
                                  <div className="detail-item-modern">
                                    <div className="detail-label">{t('bids.interestAvailableForRolling')}</div>
                                    <div className="detail-value">{formatCurrencyWithSymbol(bid.interestAvailableForRolling, request.currency || 'EUR')}</div>
                                  </div>
                                )}

                                {bid.notes && (
                                  <div className="detail-item-modern" style={{ gridColumn: '1 / -1' }}>
                                    <div className="detail-label">{t('requestDetail.bidDetails.bankNotes')}</div>
                                    <div className="detail-value">{bid.notes}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {(request.currency === 'EUR' || !request.currency) && getBenchmarkForDuration(request.duration) && (
                  <tr style={{ background: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: '#495057' }}>
                        {getBenchmarkForDuration(request.duration)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>Benchmark</div>
                    </td>
                    <td>
                      <div className="interest-rate" style={{ color: '#495057' }}>
                        {benchmarkRate !== null
                          ? `${Number(benchmarkRate).toFixed(3)}%`
                          : '—'}
                      </div>
                      {benchmarkDate && (
                        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                          al {benchmarkDate}
                        </div>
                      )}
                    </td>
                    <td colSpan={depositColumnCount - 2}></td>
                  </tr>
                )}
              </tbody>
              {request.status === 'pending_allocation' && (() => {
                const totalInterest = getTotalInterest();
                return (
                  <tfoot>
                    <tr className="dep-totals-row">
                      <td>{t('requestDetail.bidsSection.total')}</td>
                      <td colSpan="3"></td>
                      <td>{formatCurrencyWithSymbol(totalAllocated, request.currency || 'EUR')}</td>
                      <td></td>
                      <td className="dep-interest-cell dep-interest-start dep-interest-total">
                        {totalInterest !== null ? formatCostAmount(totalInterest, request.currency || 'EUR') : t('bids.notAvailable')}
                      </td>
                    </tr>
                  </tfoot>
                );
              })()}
              {request.status === 'completed' && (
                <tfoot>
                  <tr className="dep-totals-row">
                    <td>{t('allocation.totalAllocated')}</td>
                    <td colSpan="4"></td>
                    <td>{formatCurrencyWithSymbol(getTotalAllocatedAmount(), request.currency || 'EUR')}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Mobile Cards View */}
          <div className="mobile-cards-container">
            {bids.map(bid => renderMobileBidCard(bid))}
            {(request.currency === 'EUR' || !request.currency) && getBenchmarkForDuration(request.duration) && (
              <div className="mobile-record-card" style={{ background: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
                <div className="mobile-card-header">
                  <div className="mobile-card-title" style={{ fontWeight: 600, color: '#495057' }}>
                    {getBenchmarkForDuration(request.duration)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>Benchmark</div>
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('bids.interestRate')}</span>
                    <span className="mobile-card-value">
                      <span className="interest-rate" style={{ color: '#495057' }}>
                        {benchmarkRate !== null
                          ? `${Number(benchmarkRate).toFixed(3)}%`
                          : '—'}
                      </span>
                      {benchmarkDate && (
                        <span style={{ fontSize: '11px', color: '#6c757d', display: 'block', marginTop: '2px' }}>
                          al {benchmarkDate}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {request.status === 'completed' && (
              <div className="mobile-record-card dep-totals-card">
                <div className="mobile-card-body">
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('allocation.totalAllocated')}</span>
                    <span className="mobile-card-value">{formatCurrencyWithSymbol(getTotalAllocatedAmount(), request.currency || 'EUR')}</span>
                  </div>
                </div>
              </div>
            )}
            {request.status === 'pending_allocation' && (
              <div className="mobile-record-card dep-totals-card">
                <div className="mobile-card-header">
                  <div className="mobile-card-title">{t('requestDetail.bidsSection.total')}</div>
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('allocation.totalAllocated')}</span>
                    <span className="mobile-card-value">{formatCurrencyWithSymbol(totalAllocated, request.currency || 'EUR')}</span>
                  </div>
                  <div className="mobile-card-row dep-interest-cell">
                    <span className="mobile-card-label">{interestLabel()}</span>
                    <span className="mobile-card-value dep-interest-total">
                      {getTotalInterest() !== null ? formatCostAmount(getTotalInterest(), request.currency || 'EUR') : t('bids.notAvailable')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {request.status === 'pending_allocation' && allocationError && (
            <div className="allocation-error">{allocationError}</div>
          )}
        </>

        ) : (
          <div className="alert-info">
            <p>{t('requestDetail.bidsSection.noBidsYet')}</p>
          </div>
        )}
      </div>
      
      {request.status === 'completed' && (
        <div className="alert-success">
          <p>{t('requestDetail.messages.requestCompletedSuccess')}</p>
        </div>
      )}
      
      {request.status === 'cancelled' && (
        <div className="alert-danger">
          <p>{t('requestDetail.messages.requestCancelledInfo')}</p>
        </div>
      )}
      
      <div className="request-actions">
        {request.status === 'bidding' && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => updateRequestStatus('pending_allocation')}
            disabled={processingAction || !hasActiveBids(bids)}
            title={!hasActiveBids(bids) ? t('requestDetail.actions.atLeastOneBidRequired') : ""}
          >
            {processingAction ? t('actions.processing') : t('actions.closeBiddingProceed')}
          </button>
        )}
        
        {request.status === 'pending_allocation' && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirmAllocation}
            disabled={!!allocationError || processingAllocation || processingAction}
          >
            {processingAllocation ? t('allocation.processing') : t('allocation.confirmAllocation')}
          </button>
        )}

        {['bidding', 'pending_allocation'].includes(request.status) && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => updateRequestStatus('cancelled')}
            disabled={processingAction || processingAllocation}
          >
            {processingAction ? t('actions.processing') : t('actions.cancelRequest')}
          </button>
        )}
      </div>
    </div>

    {/* TODO: Commissioni - da abilitare in futuro
    {showConfirmModal && commissionInfo && commissionInfo.isPaying === false && (
      <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
        <div className="modal-content commission-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{t('commission.confirmAllocation') || 'Conferma Allocazione'}</h3>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowConfirmModal(false)}
            >
              &times;
            </button>
          </div>
          <div className="modal-body">
            {commissionPreviewData && (
              <>
                <div className="commission-summary">
                  <h4>{t('commission.allocationSummary') || 'Riepilogo Allocazione'}</h4>

                  {commissionPreviewData.allocations.map((alloc, index) => (
                    <div key={index} className="commission-allocation-item">
                      <div className="allocation-bank-name">{alloc.bankName}</div>
                      <div className="allocation-details-grid">
                        <div className="detail-row">
                          <span className="detail-label">{t('bids.allocatedAmount') || 'Importo allocato'}:</span>
                          <span className="detail-value">{formatCurrencyWithSymbol(alloc.amount, request.currency || 'EUR')}</span>
                        </div>
                        {!commissionPreviewData.isOvernight && (
                          <div className="detail-row">
                            <span className="detail-label">{t('bids.interestRate') || 'Tasso interesse'}:</span>
                            <span className="detail-value">
                              {alloc.rateType === 'floating' && alloc.baseRate
                                ? `${formatBaseRate(alloc.baseRate)} ${alloc.interestRate >= 0 ? '+' : ''}${alloc.interestRate?.toFixed(2)}%`
                                : `${alloc.interestRate?.toFixed(2)}%`
                              }
                            </span>
                          </div>
                        )}
                        <div className="detail-row">
                          <span className="detail-label">{t('requests.maturity') || 'Durata'}:</span>
                          <span className="detail-value">{formatDuration(request.duration, t)}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="commission-totals">
                    <div className="total-row">
                      <span className="total-label">{t('allocation.totalAllocated') || 'Totale allocato'}:</span>
                      <span className="total-value">{formatCurrencyWithSymbol(commissionPreviewData.totals.amount, request.currency || 'EUR')}</span>
                    </div>

                    {!commissionPreviewData.isOvernight && commissionPreviewData.totals.guadagnoStimato !== null && (
                      <div className="total-row earnings-row">
                        <span className="total-label">{t('commission.estimatedEarnings') || 'Guadagno lordo stimato'}:</span>
                        <span className="total-value earnings-value">
                          {formatCurrencyWithSymbol(commissionPreviewData.totals.guadagnoStimato, request.currency || 'EUR')}
                        </span>
                      </div>
                    )}

                    <div className="total-row commission-row">
                      <span className="total-label">{t('commission.euracleCommission') || 'Commissione Euracle'}:</span>
                      <span className="total-value commission-value">
                        {formatCurrencyWithSymbol(commissionPreviewData.totals.costoGestione, 'EUR')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="commission-disclaimer">
                  <Info size={16} />
                  <span>
                    {commissionPreviewData.isOvernight
                      ? (t('commission.overnightDisclaimer') || 'Per i conti correnti (overnight) si applica una commissione fissa di 100€.')
                      : (t('commission.depositDisclaimer') || 'Il guadagno lordo stimato è indicativo e non tiene conto di tasse o altri costi.')}
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowConfirmModal(false)}
              disabled={processingAllocation}
            >
              {t('common.cancel') || 'Annulla'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={executeAllocation}
              disabled={processingAllocation}
            >
              {processingAllocation ? (t('allocation.processing') || 'Elaborazione...') : (t('allocation.confirm') || 'Conferma')}
            </button>
          </div>
        </div>
      </div>
    )}
    */}
  </div>
);
};

export default RequestDetail;